import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  DownloadCloud,
  KeyRound,
  RefreshCw,
  Send,
  ShieldOff,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Role, UserInviteResult } from '@khadi/shared';
import { api, ApiError, type ListResponse } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDateTime } from '../lib/format';
import { PageHeader, Spinner, cn } from '../components/ui';

interface UserRow {
  id: number;
  email: string;
  name: string;
  role: Role;
  is_active: boolean;
  totp_enrolled: boolean;
  passkeys: number;
  invite_pending: boolean;
  invite_expires_at: string | null;
  last_login_at: string | null;
  created_at: string;
}

interface TokenRow {
  id: number;
  name: string;
  role: Role;
  scopes: string[];
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

interface ImportResult {
  dry_run: boolean;
  duration_ms: number;
  sections: { name: string; fetched: number; created: number; updated: number; skipped: number }[];
  warnings: string[];
}

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Yagemi Admin',
  manager: 'Khadi Manager',
  viewer: 'Khadi Leser',
};

/** Verwaltung: Konten, Maschinenzugänge, Import und Protokoll. */
export function AdminPage() {
  const [tab, setTab] = useState<'users' | 'tokens' | 'import' | 'connectors' | 'audit'>('users');

  return (
    <div>
      <PageHeader
        eyebrow="Nur für Yagemi"
        title="Verwaltung"
        subtitle="Zugänge, Schnittstellen und der Datenabgleich mit Snipe-IT."
      />

      <div className="mb-5 flex flex-wrap gap-1.5" role="tablist">
        {(
          [
            ['users', 'Konten'],
            ['tokens', 'API-Zugänge'],
            ['import', 'Snipe-Import'],
            ['connectors', 'Konnektoren'],
            ['audit', 'Protokoll'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              'rounded-[var(--radius-pill)] border px-3.5 py-1.5 text-sm transition-colors',
              tab === key
                ? 'border-[var(--color-khadi-forest-800)] bg-[var(--color-khadi-forest-800)] font-semibold text-[var(--color-khadi-cream-50)]'
                : 'border-[var(--color-line)] text-[var(--color-fg2)] hover:bg-[var(--color-bg-tinted)]',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'users' && <UsersPanel />}
      {tab === 'tokens' && <TokensPanel />}
      {tab === 'import' && <ImportPanel />}
      {tab === 'connectors' && <ConnectorsPanel />}
      {tab === 'audit' && <AuditPanel />}
    </div>
  );
}

function UsersPanel() {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const [form, setForm] = useState({ email: '', name: '', role: 'viewer' as Role });
  const [inviteLink, setInviteLink] = useState<{ email: string; link: string } | null>(null);
  const [zuLoeschen, setZuLoeschen] = useState<UserRow | null>(null);
  const [faktorZuruecksetzen, setFaktorZuruecksetzen] = useState<UserRow | null>(null);

  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api<ListResponse<UserRow>>('/admin/users'),
  });

  const create = useMutation({
    mutationFn: () =>
      api<UserRow & { invite_link: string }>('/admin/users', {
        method: 'POST',
        body: { ...form, send_invite: true },
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setForm({ email: '', name: '', role: 'viewer' });
      setInviteLink({ email: created.email, link: created.invite_link });
      toast.success(`Konto für ${created.name} angelegt`);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Anlegen fehlgeschlagen'),
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api(`/admin/users/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Konto aktualisiert');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Änderung fehlgeschlagen'),
  });

  // Ein frischer Link, per Mail verschickt — und hier zum Kopieren, falls die
  // Mail nicht ankommt oder der Mailweg noch nicht steht.
  const einladen = useMutation({
    mutationFn: (user: UserRow) =>
      api<UserInviteResult>(`/admin/users/${user.id}/invite`, { method: 'POST' }),
    onSuccess: (result, user) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setInviteLink({ email: user.email, link: result.invite_link });
      if (result.mail_sent) toast.success(`Einladung an ${user.email} gesendet`);
      else toast.warning('Die E-Mail ging nicht raus — den Link bitte selbst weitergeben');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Einladung fehlgeschlagen'),
  });

  const loeschen = useMutation({
    mutationFn: (user: UserRow) => api(`/admin/users/${user.id}`, { method: 'DELETE' }),
    onSuccess: (_result, user) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setZuLoeschen(null);
      toast.success(`Konto von ${user.name} gelöscht`);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen'),
  });

  return (
    <div className="space-y-4">
      <section className="surface animate-fade-up pad">
        <h2 className="font-[family-name:var(--font-display)] mb-4 text-2xl">Konto anlegen</h2>
        <form
          className="grid gap-3 sm:grid-cols-[1fr_1fr_auto_auto]"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <input
            className="field"
            type="email"
            placeholder="E-Mail"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            required
            aria-label="E-Mail"
          />
          <input
            className="field"
            placeholder="Name"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            required
            aria-label="Name"
          />
          <select
            className="field"
            value={form.role}
            onChange={(event) => setForm({ ...form, role: event.target.value as Role })}
            aria-label="Rolle"
          >
            {(Object.keys(ROLE_LABELS) as Role[]).map((role) => (
              <option key={role} value={role}>
                {ROLE_LABELS[role]}
              </option>
            ))}
          </select>
          <button type="submit" className="btn btn-primary btn-md" disabled={create.isPending}>
            <UserPlus className="h-4 w-4" aria-hidden />
            Einladen
          </button>
        </form>

        {inviteLink && (
          <div className="animate-fade-in mt-3 rounded-[var(--radius-md)] bg-[var(--color-khadi-saffron-100)] px-5 py-4">
            <p className="mb-1 text-sm text-[var(--color-fg2)]">
              Einladungslink für {inviteLink.email} — sieben Tage gültig
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate text-sm">{inviteLink.link}</code>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(inviteLink.link);
                  toast.success('Link kopiert');
                }}
                className="btn btn-quiet btn-sm"
              >
                <Copy className="h-3.5 w-3.5" aria-hidden />
                kopieren
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="surface animate-fade-up overflow-hidden">
        {users.isLoading && <Spinner />}
        <ul className="divide-y divide-[var(--color-line)]">
          {users.data?.rows.map((user) => {
            const eigenes = user.id === auth.user?.id;
            const einladung = einladungsStand(user);
            return (
              <li key={user.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
                <span className="min-w-0 flex-1 basis-60">
                  <span className="block truncate text-[0.9375rem] font-semibold">{user.name}</span>
                  <span className="block truncate text-sm text-[var(--color-fg3)]">
                    {user.email} · zuletzt{' '}
                    {user.last_login_at ? formatDateTime(user.last_login_at) : 'nie'}
                    {einladung?.hinweis ? ` · ${einladung.hinweis}` : ''}
                  </span>
                </span>

                {user.totp_enrolled && <span className="pill pill-deployed">Authenticator</span>}
                {user.passkeys > 0 && (
                  <span className="pill pill-deployed">
                    {user.passkeys === 1 ? 'Passkey' : `${user.passkeys} Passkeys`}
                  </span>
                )}
                {!user.is_active && <span className="pill pill-archived">gesperrt</span>}
                {einladung && (
                  <span className={cn('pill', einladung.abgelaufen ? 'pill-archived' : 'pill-stock')}>
                    {einladung.label}
                  </span>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className="field h-9 w-auto text-sm"
                    value={user.role}
                    onChange={(event) =>
                      update.mutate({ id: user.id, body: { role: event.target.value } })
                    }
                    aria-label={`Rolle von ${user.name}`}
                  >
                    {(Object.keys(ROLE_LABELS) as Role[]).map((role) => (
                      <option key={role} value={role}>
                        {ROLE_LABELS[role]}
                      </option>
                    ))}
                  </select>

                  {/* Das eigene Konto lädt man nicht neu ein und löscht es nicht —
                      das nimmt dem Admin nicht versehentlich den Boden weg. */}
                  {!eigenes && (
                    <button
                      type="button"
                      onClick={() => einladen.mutate(user)}
                      disabled={einladen.isPending}
                      className="btn btn-ghost btn-sm"
                      title={
                        user.invite_pending
                          ? 'Neuen Einladungslink schicken'
                          : 'Neuen Link zur Passwortvergabe schicken'
                      }
                    >
                      <Send className="h-4 w-4" aria-hidden />
                      Einladung erneut senden
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() =>
                      update.mutate({ id: user.id, body: { is_active: !user.is_active } })
                    }
                    className="btn btn-ghost btn-sm"
                  >
                    {user.is_active ? 'sperren' : 'entsperren'}
                  </button>

                  {/* Der Rettungsweg bei verlorenem Handy: Ohne Passkey verlangt
                      die Anmeldung ihn zwingend — nur ein anderer Admin kann ihn entfernen. */}
                  {!eigenes && (user.totp_enrolled || user.passkeys > 0) && (
                    <button
                      type="button"
                      onClick={() => setFaktorZuruecksetzen(user)}
                      className="btn btn-ghost btn-sm"
                      title="Passkeys und Authenticator entfernen"
                    >
                      <ShieldOff className="h-4 w-4" aria-hidden />
                      2FA zurücksetzen
                    </button>
                  )}

                  {!eigenes && (
                    <button
                      type="button"
                      onClick={() => setZuLoeschen(user)}
                      className="btn btn-danger btn-sm"
                      aria-label={`Konto von ${user.name} löschen`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                      löschen
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {zuLoeschen && (
        <KontoLoeschenDialog
          user={zuLoeschen}
          pending={loeschen.isPending}
          onConfirm={() => loeschen.mutate(zuLoeschen)}
          onClose={() => setZuLoeschen(null)}
        />
      )}

      {faktorZuruecksetzen && (
        <RueckfrageDialog
          titel="Zweiten Faktor zurücksetzen?"
          bestaetigung="Zurücksetzen"
          pending={update.isPending}
          onConfirm={() =>
            update.mutate(
              {
                id: faktorZuruecksetzen.id,
                body: { reset_totp: true, reset_passkeys: true },
              },
              { onSuccess: () => setFaktorZuruecksetzen(null) },
            )
          }
          onClose={() => setFaktorZuruecksetzen(null)}
        >
          <p className="text-[var(--color-fg2)]">
            Passkeys und Authenticator von{' '}
            <span className="font-semibold text-[var(--color-fg)]">{faktorZuruecksetzen.name}</span>{' '}
            werden entfernt, laufende Sitzungen beendet.
          </p>
          <p className="mt-2 text-sm text-[var(--color-fg3)]">
            Das Passwort bleibt. Ein Verwaltungskonto richtet beim nächsten Anmelden einen neuen
            zweiten Faktor ein.
          </p>
        </RueckfrageDialog>
      )}
    </div>
  );
}

/** Ob ein Einladungslink offen ist — und ob er noch gilt. */
function einladungsStand(
  user: UserRow,
): { label: string; hinweis: string; abgelaufen: boolean } | null {
  if (!user.invite_expires_at) return null;
  const abgelaufen = new Date(user.invite_expires_at) < new Date();
  return {
    abgelaufen,
    label: abgelaufen ? 'Einladung abgelaufen' : 'Einladung offen',
    hinweis: abgelaufen ? '' : `Link gültig bis ${formatDate(user.invite_expires_at)}`,
  };
}

/** Eingriffe, die sich nicht rückgängig machen lassen, bekommen eine echte Rückfrage. */
function RueckfrageDialog({
  titel,
  bestaetigung,
  pending,
  onConfirm,
  onClose,
  children,
}: {
  titel: string;
  bestaetigung: ReactNode;
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children: ReactNode;
}) {
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-[var(--color-khadi-forest-900)]/45 backdrop-blur-[2px] sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={titel}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="animate-sheet-up safe-bottom w-full max-w-md rounded-t-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-bg-elevated)] p-5 shadow-[var(--shadow-lg)] sm:rounded-[var(--radius-xl)]">
        <h2 className="font-[family-name:var(--font-display)] mb-3 text-2xl">{titel}</h2>
        {children}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-md">
            Abbrechen
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="btn btn-danger btn-md"
            autoFocus
          >
            {bestaetigung}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Löschen ist endgültig — deshalb eine echte Rückfrage statt eines Sofortklicks. */
function KontoLoeschenDialog({
  user,
  pending,
  onConfirm,
  onClose,
}: {
  user: UserRow;
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <RueckfrageDialog
      titel="Konto löschen?"
      bestaetigung={
        <>
          <Trash2 className="h-4 w-4" aria-hidden />
          Konto löschen
        </>
      }
      pending={pending}
      onConfirm={onConfirm}
      onClose={onClose}
    >
      <p className="text-[var(--color-fg2)]">
        <span className="font-semibold text-[var(--color-fg)]">{user.name}</span> ({user.email})
        verliert den Zugang sofort. Sitzungen und Passkeys werden mit entfernt.
      </p>
      <p className="mt-2 text-sm text-[var(--color-fg3)]">
        Vorgänge und Protokoll behalten den Namen, ein Mitarbeiterstammsatz bleibt bestehen. Wer
        nur vorübergehend nicht hinein soll, wird besser gesperrt.
      </p>
    </RueckfrageDialog>
  );
}

function TokensPanel() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [fresh, setFresh] = useState<string | null>(null);

  const tokens = useQuery({
    queryKey: ['admin-tokens'],
    queryFn: () => api<ListResponse<TokenRow>>('/admin/tokens'),
  });

  const create = useMutation({
    mutationFn: () =>
      api<TokenRow & { token: string }>('/admin/tokens', {
        method: 'POST',
        body: { name, role, scopes: [] },
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-tokens'] });
      setFresh(created.token);
      setName('');
      toast.success('Zugang angelegt — Token jetzt kopieren');
    },
  });

  const revoke = useMutation({
    mutationFn: (id: number) => api(`/admin/tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-tokens'] });
      toast.success('Zugang widerrufen');
    },
  });

  return (
    <div className="space-y-4">
      <section className="surface animate-fade-up pad">
        <h2 className="font-[family-name:var(--font-display)] mb-1 text-xl">Maschinenzugänge</h2>
        <p className="mb-3 text-sm text-[var(--color-fg3)]">
          Für den MCP-Server in Claude Code, den Notion-Abgleich und Skripte. Der Token wird nur
          einmal angezeigt.
        </p>

        <form
          className="grid gap-3 sm:grid-cols-[1fr_auto_auto]"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <input
            className="field"
            placeholder="Bezeichnung, z. B. Claude Code"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            aria-label="Bezeichnung"
          />
          <select
            className="field"
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
            aria-label="Rolle"
          >
            {(Object.keys(ROLE_LABELS) as Role[]).map((entry) => (
              <option key={entry} value={entry}>
                {ROLE_LABELS[entry]}
              </option>
            ))}
          </select>
          <button type="submit" className="btn btn-primary btn-md" disabled={!name.trim()}>
            <KeyRound className="h-4 w-4" aria-hidden />
            Ausstellen
          </button>
        </form>

        {fresh && (
          <div className="animate-fade-in mt-3 rounded-[var(--radius-md)] border border-[var(--color-khadi-saffron-400)] bg-[var(--color-khadi-saffron-100)] p-3">
            <p className="mb-1.5 text-sm font-semibold">
              Jetzt kopieren — danach ist der Token nicht mehr einsehbar:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate font-[family-name:var(--font-mono)] text-sm">
                {fresh}
              </code>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(fresh);
                  toast.success('Token kopiert');
                }}
                className="btn btn-quiet btn-sm"
              >
                <Copy className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="surface animate-fade-up overflow-hidden">
        <ul className="divide-y divide-[var(--color-line)]">
          {tokens.data?.rows.map((token) => (
            <li key={token.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[0.9375rem] font-semibold">{token.name}</span>
                <span className="block text-sm text-[var(--color-fg3)]">
                  {ROLE_LABELS[token.role]} · zuletzt genutzt{' '}
                  {token.last_used_at ? formatDateTime(token.last_used_at) : 'nie'}
                </span>
              </span>
              {token.revoked_at ? (
                <span className="pill pill-archived">widerrufen</span>
              ) : (
                <button
                  type="button"
                  onClick={() => revoke.mutate(token.id)}
                  className="btn btn-ghost btn-sm"
                >
                  widerrufen
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ImportPanel() {
  const [result, setResult] = useState<ImportResult | null>(null);

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api<ImportResult>('/admin/import/snipe', {
        method: 'POST',
        body: { dry_run: dryRun, include: ['stammdaten', 'employees', 'assets', 'movements'] },
      }),
    onSuccess: (data) => {
      setResult(data);
      toast.success(data.dry_run ? 'Probelauf abgeschlossen' : 'Import abgeschlossen');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Der Import ist fehlgeschlagen'),
  });

  return (
    <div className="space-y-4">
      <section className="surface animate-fade-up pad">
        <h2 className="font-[family-name:var(--font-display)] mb-1 text-xl">Abgleich mit Snipe-IT</h2>
        <p className="mb-4 text-sm text-[var(--color-fg3)]">
          Beliebig oft wiederholbar. Vorhandene Datensätze werden über ihre Snipe-ID erkannt und
          aktualisiert, nicht doppelt angelegt. Die Inventarnummern bleiben unverändert.
        </p>

        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => run.mutate(true)}
            disabled={run.isPending}
            className="btn btn-ghost btn-md flex-1"
          >
            <RefreshCw className={cn('h-4 w-4', run.isPending && 'animate-spin')} aria-hidden />
            Probelauf
          </button>
          <button
            type="button"
            onClick={() => run.mutate(false)}
            disabled={run.isPending}
            className="btn btn-primary btn-md flex-1"
          >
            <DownloadCloud className="h-4 w-4" aria-hidden />
            Import ausführen
          </button>
        </div>
      </section>

      {result && (
        <section className="surface animate-fade-up overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-line)] bg-[var(--color-bg-tinted)]/60 text-left">
                <th className="px-5 py-3.5 font-semibold">Bereich</th>
                <th className="px-5 py-3.5 text-right font-semibold">geholt</th>
                <th className="px-5 py-3.5 text-right font-semibold">neu</th>
                <th className="px-5 py-3.5 text-right font-semibold">geändert</th>
                <th className="px-5 py-3.5 text-right font-semibold">übersprungen</th>
              </tr>
            </thead>
            <tbody>
              {result.sections.map((section) => (
                <tr key={section.name} className="border-b border-[var(--color-line)] last:border-0">
                  <td className="px-4 py-2">{section.name}</td>
                  <td className="tabular px-4 py-2 text-right">{section.fetched}</td>
                  <td className="tabular px-4 py-2 text-right font-semibold">{section.created}</td>
                  <td className="tabular px-4 py-2 text-right">{section.updated}</td>
                  <td className="tabular px-4 py-2 text-right text-[var(--color-fg3)]">
                    {section.skipped}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {result.warnings.length > 0 && (
            <div className="border-t border-[var(--color-line)] px-5 py-4">
              <p className="mb-1 text-sm font-semibold">Hinweise</p>
              <ul className="space-y-0.5 text-sm text-[var(--color-fg2)]">
                {result.warnings.slice(0, 10).map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

interface ConnectorRow {
  id: number;
  kind: string;
  name: string;
  is_enabled: boolean;
  last_run_at: string | null;
  last_error: string | null;
  synced_items: number;
  config?: {
    parent_page_id?: string;
    assets_database_id?: string;
    employees_database_id?: string;
  };
}

/**
 * Konnektoren — derzeit der Notion-Spiegel.
 * Der Abgleich läuft einseitig: Notion wird beschrieben, nie gelesen.
 */
function ConnectorsPanel() {
  const queryClient = useQueryClient();
  const [pageIds, setPageIds] = useState<Record<number, string>>({});

  const connectors = useQuery({
    queryKey: ['admin-connectors'],
    queryFn: () => api<ListResponse<ConnectorRow>>('/admin/connectors'),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      api(`/admin/connectors/${id}`, { method: 'PATCH', body: { is_enabled: enabled } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-connectors'] });
      toast.success('Konnektor aktualisiert');
    },
  });

  /**
   * Notion-Ziel hinterlegen. Ob Seite oder Datenbank gemeint ist, stellt der
   * Server fest — die Kennungen sehen in Notion identisch aus.
   */
  const saveParentPage = useMutation({
    mutationFn: ({ id, pageId }: { id: number; pageId: string }) =>
      api<{ art: 'database' | 'page'; titel: string }>(`/admin/connectors/${id}/notion-ziel`, {
        method: 'POST',
        body: { notion_id: pageId.trim() },
      }),
    onSuccess: (ergebnis) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-connectors'] });
      toast.success(
        ergebnis.art === 'database'
          ? `Datenbank „${ergebnis.titel}" übernommen — sie wird beim Abgleich um fehlende Felder ergänzt`
          : `Seite „${ergebnis.titel}" hinterlegt — die Datenbanken entstehen beim Abgleich`,
      );
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen'),
  });

  const [pruefung, setPruefung] = useState<
    Record<number, { name: string; ok: boolean; hinweis: string }[]>
  >({});

  const pruefen = useMutation({
    mutationFn: (id: number) =>
      api<{ schritte: { name: string; ok: boolean; hinweis: string }[] }>(
        `/admin/connectors/${id}/test`,
        { method: 'POST' },
      ),
    onSuccess: (ergebnis, id) => {
      setPruefung({ ...pruefung, [id]: ergebnis.schritte });
      const offen = ergebnis.schritte.filter((s) => !s.ok);
      if (offen.length === 0) toast.success('Verbindung steht');
      else toast.warning(`${offen.length} Punkt(e) offen — siehe Liste`);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Prüfung fehlgeschlagen'),
  });

  const sync = useMutation({
    mutationFn: (id: number) =>
      api<{ created: number; updated: number; skipped: number }>(
        `/admin/connectors/${id}/sync`,
        { method: 'POST' },
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-connectors'] });
      toast.success(
        `Abgleich fertig: ${result.created} neu, ${result.updated} aktualisiert, ${result.skipped} unverändert`,
      );
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Der Abgleich ist fehlgeschlagen'),
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--color-fg3)]">
        Konnektoren spiegeln den Bestand in andere Werkzeuge. Der Abgleich ist einseitig —
        Änderungen dort werden beim nächsten Lauf überschrieben.
      </p>

      {connectors.isLoading && <Spinner />}

      {connectors.data?.rows.map((connector) => (
        <section key={connector.id} className="surface animate-fade-up pad">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="font-[family-name:var(--font-display)] text-2xl">{connector.name}</h2>
              <p className="mt-0.5 text-sm text-[var(--color-fg3)]">
                {connector.synced_items} gespiegelte Geräte ·{' '}
                {connector.last_run_at
                  ? `zuletzt ${formatDateTime(connector.last_run_at)}`
                  : 'noch nie gelaufen'}
              </p>
              {connector.last_error && (
                <p className="mt-1 text-sm text-[var(--color-khadi-clay-700)]">
                  {connector.last_error}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => toggle.mutate({ id: connector.id, enabled: !connector.is_enabled })}
                className={cn('btn btn-sm', connector.is_enabled ? 'btn-ghost' : 'btn-primary')}
              >
                {connector.is_enabled ? 'ausschalten' : 'einschalten'}
              </button>
              <button
                type="button"
                onClick={() => pruefen.mutate(connector.id)}
                disabled={pruefen.isPending}
                className="btn btn-ghost btn-sm"
              >
                Verbindung prüfen
              </button>
              <button
                type="button"
                onClick={() => sync.mutate(connector.id)}
                disabled={!connector.is_enabled || sync.isPending}
                className="btn btn-primary btn-sm"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', sync.isPending && 'animate-spin')} aria-hidden />
                Vollabgleich
              </button>
            </div>
          </div>

          {/* Ergebnis der Verbindungsprüfung — sagt konkret, woran es hakt. */}
          {pruefung[connector.id] && (
            <ul className="animate-fade-up mt-4 space-y-2 border-t border-[var(--color-line)] pt-4">
              {pruefung[connector.id]!.map((schritt) => (
                <li key={schritt.name} className="flex items-start gap-2.5">
                  <span
                    aria-hidden
                    className={cn(
                      'mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full',
                      schritt.ok
                        ? 'bg-[var(--color-khadi-forest-600)]'
                        : 'bg-[var(--color-accent)]',
                    )}
                  />
                  <span className="min-w-0">
                    <span className="block font-semibold text-[var(--color-fg1)]">
                      {schritt.name}
                    </span>
                    <span className="block text-sm text-[var(--color-fg3)]">{schritt.hinweis}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}

          {connector.kind === 'notion' && (
            <div className="mt-5 border-t border-[var(--color-line)] pt-4">
              {connector.config?.assets_database_id ? (
                <p className="rounded-[var(--radius-md)] bg-[var(--color-khadi-forest-100)] px-5 py-4 text-[var(--color-fg1)]">
                  Die Datenbanken „Khadi Geräte" und „Khadi Mitarbeiter" sind in Notion angelegt
                  und miteinander verknüpft. Der Vollabgleich hält sie aktuell.
                </p>
              ) : (
                <>
                  <span className="label">Notion-Ziel</span>
                  <p className="mb-3 text-sm text-[var(--color-fg3)]">
                    Entweder die Kennung einer <strong>Seite</strong> — dann entstehen dort beim
                    ersten Abgleich zwei verbundene Datenbanken — oder die Kennung einer{' '}
                    <strong>bestehenden Datenbank</strong>, die dann weiterverwendet und um
                    fehlende Felder ergänzt wird. Was von beidem es ist, erkennt die Anwendung
                    selbst. In jedem Fall muss das Ziel in Notion über <em>⋯ → Verbindungen</em>{' '}
                    mit der Integration geteilt sein.
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      className="field tabular"
                      placeholder="Seiten- oder Datenbank-Kennung, 32 Zeichen"
                      value={
                        pageIds[connector.id] ??
                        connector.config?.assets_database_id ??
                        connector.config?.parent_page_id ??
                        ''
                      }
                      onChange={(event) =>
                        setPageIds({ ...pageIds, [connector.id]: event.target.value })
                      }
                      aria-label="Notion-Seiten-ID"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        saveParentPage.mutate({
                          id: connector.id,
                          pageId: pageIds[connector.id] ?? '',
                        })
                      }
                      disabled={
                        !(pageIds[connector.id] ?? '').trim() || saveParentPage.isPending
                      }
                      className="btn btn-primary btn-md shrink-0"
                    >
                      {saveParentPage.isPending ? 'Wird geprüft …' : 'Übernehmen'}
                    </button>
                  </div>
                  <p className="mt-3 text-sm text-[var(--color-fg3)]">
                    Der Integrations-Token gehört als <code>NOTION_TOKEN</code> in die Umgebung —
                    er wird nie angezeigt.
                  </p>
                </>
              )}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

function AuditPanel() {
  const audit = useQuery({
    queryKey: ['admin-audit'],
    queryFn: () =>
      api<
        ListResponse<{
          id: number;
          entity: string;
          entity_id: number | null;
          action: string;
          actor: string | null;
          source: string;
          created_at: string;
        }>
      >('/admin/audit', { query: { limit: 100 } }),
  });

  return (
    <section className="surface animate-fade-up overflow-hidden">
      {audit.isLoading && <Spinner />}
      <ul className="divide-y divide-[var(--color-line)]">
        {audit.data?.rows.map((entry) => (
          <li key={entry.id} className="flex flex-wrap items-center gap-x-3 px-5 py-3.5 text-sm">
            <span className="tabular text-sm text-[var(--color-fg3)]">
              {formatDateTime(entry.created_at)}
            </span>
            <span className="font-semibold">{entry.action}</span>
            <span className="text-[var(--color-fg2)]">
              {entry.entity}
              {entry.entity_id ? ` #${entry.entity_id}` : ''}
            </span>
            <span className="ml-auto text-sm text-[var(--color-fg3)]">
              {entry.actor ?? 'System'} · {entry.source}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
